import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePrivacy } from '../contexts/PrivacyContext';
import { useRealtime } from '../contexts/RealtimeContext';
import { Settings, Shield, Laptop, Lock, Wifi, Key, Check } from 'lucide-react';

export const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { isPrivacyMode, togglePrivacyMode } = usePrivacy();
  const { device } = useRealtime();

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="w-5 h-5 text-indigo-600" />
          <span>Cockpit Settings</span>
        </h2>
        <p className="text-xs text-slate-500">
          Operator preferences, device pairing tokens, and privacy parameters
        </p>
      </div>

      {/* Privacy Mode Settings */}
      <div className="acrylic-panel p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
          <Shield className="w-4 h-4 text-indigo-600" />
          <h3 className="font-bold text-sm text-slate-800">Office Privacy & Focus Mode</h3>
        </div>

        <div className="space-y-3 text-xs">
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200/60">
            <div>
              <span className="font-semibold text-slate-800 block">Enable Privacy Mode</span>
              <span className="text-slate-500">
                Obfuscates sensitive Windows file paths, renames browser tab to "KAIZEN Workspace", and hides personal device tags.
              </span>
            </div>
            <button
              onClick={togglePrivacyMode}
              className={`px-3.5 py-1.5 rounded-lg font-semibold transition-colors ${
                isPrivacyMode
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
              }`}
            >
              {isPrivacyMode ? 'Enabled' : 'Disabled'}
            </button>
          </div>

          <div className="p-3 bg-amber-50/70 border border-amber-200/60 rounded-lg text-amber-900 text-[11px]">
            <span className="font-semibold">Shortcut:</span> Press <kbd className="px-1.5 py-0.5 bg-white border border-amber-300 rounded font-mono">Alt + P</kbd> anytime to toggle instantly from an office workstation.
          </div>
        </div>
      </div>

      {/* Registered Host Device & Allowed Roots */}
      <div className="acrylic-panel p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
          <Laptop className="w-4 h-4 text-indigo-600" />
          <h3 className="font-bold text-sm text-slate-800">Registered Windows Host Device</h3>
        </div>

        <div className="space-y-3 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60">
              <span className="text-slate-400 block mb-1">Device ID</span>
              <span className="font-mono text-slate-800 font-semibold">{device?.id || 'Unpaired'}</span>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200/60">
              <span className="text-slate-400 block mb-1">Platform Host</span>
              <span className="text-slate-800 font-semibold">{device?.osInfo || 'Windows 11 Pro'}</span>
            </div>
          </div>

          <div>
            <span className="font-semibold text-slate-700 block mb-1.5">Configured Allowed Execution Roots:</span>
            <div className="p-3 bg-slate-900 text-slate-200 font-mono text-xs rounded-lg space-y-1">
              {(device?.allowedRoots || ['F:\\KAIZEN REMOTE', 'D:\\Enabling Work Module\\']).map((root) => (
                <div key={root} className="flex items-center gap-2">
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{root}</span>
                </div>
              ))}
            </div>
            <span className="text-[11px] text-slate-400 block mt-1">
              The Windows agent will reject any job attempting to execute outside these explicitly approved directories.
            </span>
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="acrylic-panel p-5 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
          <Lock className="w-4 h-4 text-indigo-600" />
          <h3 className="font-bold text-sm text-slate-800">Operator Account</h3>
        </div>

        <div className="text-xs space-y-2">
          <div className="flex justify-between py-1 border-b border-slate-100">
            <span className="text-slate-500">Authenticated Email:</span>
            <span className="font-medium text-slate-800">{user?.email || 'operator@kaizen-remote.local'}</span>
          </div>
          <div className="flex justify-between py-1 border-b border-slate-100">
            <span className="text-slate-500">Role:</span>
            <span className="font-medium text-slate-800">Lead Implementation Engineer (Owner)</span>
          </div>
        </div>
      </div>
    </div>
  );
};
